import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { LuDownload, LuRefreshCw, LuScrollText } from 'react-icons/lu';
import { Button, Card, PageHeader, Pagination } from 'components/common';
import AuditLogFilterBar from 'components/app/authorization/audit-log/AuditLogFilterBar';
import AuditLogTable from 'components/app/authorization/audit-log/AuditLogTable';
import AuditLogDetailDrawer from 'components/app/authorization/audit-log/AuditLogDetailDrawer';
import {
  FILTER_KEYS, exportAuditLogs, fetchAuditFacets, fetchAuditLogs,
} from 'components/app/authorization/audit-log/auditLogApi';
import useFitToViewport from 'hooks/useFitToViewport';
import { useIsMobile } from 'hooks/useBreakpoints';
import { rangeError, resolveRange } from 'components/app/authorization/audit-log/auditLogRange';

const DEFAULT_PER_PAGE = 50;
const COUNT_LABEL_CAP = 10000;

// Settings → Audit Logs. Filters, page and the open event live in the URL, so
// a link to "failed sign-ins last week" or to one event can be sent as-is.
const AuditLogsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // No dates in the URL → the last 30 days, shown in the picker as such.
  const filters = useMemo(() => {
    const fromUrl = FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: searchParams.get(key) || '' }), {});
    return { ...fromUrl, ...resolveRange(fromUrl.from, fromUrl.to) };
  }, [searchParams]);
  const page = Number(searchParams.get('page')) || 1;
  const perPage = Number(searchParams.get('per_page')) || DEFAULT_PER_PAGE;
  const openId = searchParams.get('event');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const exportBusy = useRef(false);
  // One scrollbar: the table scrolls in its frame, sized to what the window has
  // left. On a phone the filters alone fill the screen, so the page scrolls.
  const pageRef  = useRef(null);
  const frameRef = useRef(null);
  const isMobile = useIsMobile();
  const tableMaxHeight = useFitToViewport(frameRef, pageRef, {
    enabled: !isMobile,
    deps: [rows.length > 0, total > 0, totalCapped, loading],
  });

  const updateParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchAuditLogs({ filters, page, perPage });
      setRows(result.rows);
      setTotal(result.total);
      setTotalCapped(result.capped);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchAuditFacets().then((data) => setActions(data.actions || [])).catch(() => setActions([]));
  }, []);

  const changeFilters = (next) => {
    const error = rangeError(next.from, next.to);
    if (error) {
      toast.error(error);
      return;
    }
    updateParams({ ...next, page: '' });
  };
  const resetFilters = () => setSearchParams(new URLSearchParams());

  const handleExport = async () => {
    if (exportBusy.current) return;
    exportBusy.current = true;
    setExporting(true);
    try {
      await exportAuditLogs(filters);
    } catch (error) {
      toast.error('Export failed.');
    } finally {
      exportBusy.current = false;
      setExporting(false);
    }
  };

  return (
    <div ref={pageRef} className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        icon={LuScrollText}
        title="Audit Logs"
        subtitle="Sign-ins, exports, permission and setting changes, and business changes across the portal."
        actions={(
          <div className="tw-flex tw-gap-2">
            <Button variant="soft" leftIcon={LuRefreshCw} onClick={load} loading={loading}>Refresh</Button>
            <Button leftIcon={LuDownload} onClick={handleExport} loading={exporting}>Export CSV</Button>
          </div>
        )}
      />

      <Card>
        <div className="tw-flex tw-flex-col tw-gap-3">
          <AuditLogFilterBar
            filters={filters}
            actions={actions}
            onChange={changeFilters}
            onReset={resetFilters}
            hasCustomRange={!!(searchParams.get('from') || searchParams.get('to'))}
          />
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs tw-text-slate-500">
            <span>
              <span className="tw-font-semibold tw-text-slate-700">
                {totalCapped ? `${COUNT_LABEL_CAP.toLocaleString()}+` : total.toLocaleString()}
              </span>
              {' events · '}
              {dayjs(filters.from).format('DD MMM YYYY')} – {dayjs(filters.to).format('DD MMM YYYY')}
            </span>
            {totalCapped && <span>Narrow the filters to see an exact count.</span>}
          </div>
          <div ref={frameRef}>
            <AuditLogTable
              rows={rows}
              loading={loading}
              maxHeight={tableMaxHeight}
              onOpen={(row) => updateParams({ event: row.id })}
            />
          </div>
          {total > 0 && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / perPage)}
              totalResults={total}
              perPage={perPage}
              pageSizeOptions={[25, 50, 100, 200]}
              onPageChange={(p) => updateParams({ page: p })}
              onPageSizeChange={(size) => updateParams({ per_page: size, page: '' })}
            />
          )}
        </div>
      </Card>

      <AuditLogDetailDrawer auditId={openId} onHide={() => updateParams({ event: '' })} />
    </div>
  );
};

export default AuditLogsPage;
